import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { tokens } from '../styles/tokens';
import { shared } from '../styles/shared';

/**
 * Avatar Sarah + bulle de message avec effet typing.
 *
 * Séquence :
 *  1. Indicateur 3 dots qui rebondissent (~500ms).
 *  2. Écriture progressive du message caractère par caractère. Les balises
 *     HTML (<p>, <b>, <strong>) sont insérées en bloc — on ne tape que les
 *     caractères de texte. La vitesse est adaptative : un long message ne
 *     dépasse jamais ~1800ms d'écriture.
 *  3. Émission de `typing-done` à la fin pour que le parent affiche les
 *     choix/formulaire/bouton.
 *
 * Si l'utilisateur préfère désactiver l'effet (accessibility, perf), il peut
 * passer l'attribut `instant` pour bypass le typing.
 */
@customElement('sarah-speaker')
export class SarahSpeaker extends LitElement {
  static styles = [
    tokens,
    shared,
    css`
      :host { display: block; }

      .speaker { display: flex; align-items: flex-start; }

      .speaker-avatar {
        position: relative;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        margin-right: 16px;
        border-radius: 50%;
      }
      .speaker-avatar img {
        display: block;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        object-fit: cover;
      }
      .speaker-avatar::after {
        position: absolute;
        right: -2px;
        bottom: -2px;
        width: 8px;
        height: 8px;
        border: 3px solid #fff;
        border-radius: 50%;
        background-color: #25d0a5;
        content: "";
      }
      @media (min-width: 720px) {
        .speaker-avatar { width: 56px; height: 56px; }
        .speaker-avatar::after { width: 10px; height: 10px; }
      }

      .speaker-message { flex: 1; min-width: 0; font-size: 18px; }
      .speaker-message.is-skippable { cursor: pointer; }
      .speaker-name {
        font-family: var(--sarah-font-display);
        font-weight: 800;
        font-size: 18px;
        line-height: 22px;
        margin: 0;
      }
      .speaker-msg {
        margin-top: 8px;
        font-size: 18px;
        line-height: 1.5;
        text-align: justify;
      }
      .speaker-msg p { margin: 0 0 12px; }
      .speaker-msg p:last-child { margin-bottom: 0; }
      @media (max-width: 719px) {
        .speaker-msg { font-size: 16px; }
      }

      /* Bulle des 3 dots */
      .typing-dots {
        display: inline-flex;
        gap: 6px;
        padding: 14px 18px;
        background: #f0f1f4;
        border-radius: 20px;
        margin-top: 8px;
        align-items: center;
      }
      .typing-dots span {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #8a8d96;
        animation: typing-dot 1.4s infinite ease-in-out;
      }
      .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
      .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
      @keyframes typing-dot {
        0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
        30%           { transform: translateY(-6px); opacity: 1; }
      }
    `,
  ];

  @property({ type: String }) message = '';
  @property({ type: String, attribute: 'avatar-src' }) avatarSrc = '';
  @property({ type: Boolean }) instant = false;

  @state() private phase: 'dots' | 'typing' | 'done' = 'dots';
  @state() private displayed = '';

  private timers: number[] = [];

  /** Délai initial où on n'affiche que les 3 dots. */
  private static readonly DOTS_MS = 500;
  /** Durée max de l'écriture progressive (texte plus long → typing plus rapide). */
  private static readonly TYPING_MAX_MS = 1800;
  /** Délai minimal entre 2 caractères (texte très court). */
  private static readonly CHAR_DELAY_MIN_MS = 18;

  connectedCallback(): void {
    super.connectedCallback();
    this.startSequence();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.clearTimers();
  }

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('message')) {
      this.clearTimers();
      this.phase = 'dots';
      this.displayed = '';
      this.startSequence();
    }
  }

  private clearTimers(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }

  private addTimer(fn: () => void, ms: number): void {
    this.timers.push(window.setTimeout(fn, ms));
  }

  private startSequence(): void {
    if (!this.message) {
      this.phase = 'done';
      this.emit();
      return;
    }
    const reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.instant || reducedMotion) {
      this.displayed = this.message;
      this.phase = 'done';
      this.emit();
      return;
    }

    // 1. Phase dots.
    this.phase = 'dots';
    this.addTimer(() => {
      // 2. Phase typing.
      this.phase = 'typing';
      const chunks = this.htmlChunks(this.message);
      const textChars = chunks.filter((c) => !c.isTag).reduce((n, c) => n + c.text.length, 0);
      const delayPerChar = textChars > 0
        ? Math.max(SarahSpeaker.CHAR_DELAY_MIN_MS, SarahSpeaker.TYPING_MAX_MS / textChars)
        : SarahSpeaker.CHAR_DELAY_MIN_MS;

      let acc = '';
      let cursor = 0;
      const playNextChunk = () => {
        if (cursor >= chunks.length) {
          this.phase = 'done';
          this.emit();
          return;
        }
        const chunk = chunks[cursor++];
        if (chunk.isTag) {
          acc += chunk.text;
          this.displayed = acc;
          // Pas de délai pour les balises — chaîne immédiatement la suite.
          this.addTimer(playNextChunk, 0);
          return;
        }
        // Texte : on tape lettre par lettre.
        let i = 0;
        const playChar = () => {
          if (i >= chunk.text.length) {
            this.addTimer(playNextChunk, 0);
            return;
          }
          acc += chunk.text[i++];
          this.displayed = acc;
          this.addTimer(playChar, delayPerChar);
        };
        playChar();
      };
      playNextChunk();
    }, SarahSpeaker.DOTS_MS);
  }

  private emit(): void {
    this.dispatchEvent(
      new CustomEvent('typing-done', { bubbles: true, composed: true }),
    );
  }

  private skipToEnd(): void {
    if (this.phase === 'done') return;
    this.clearTimers();
    this.displayed = this.message;
    this.phase = 'done';
    this.emit();
  }

  /**
   * Découpe le HTML en chunks "texte" / "balise". Permet de typer les
   * caractères de texte un par un tout en insérant les balises en bloc — le
   * navigateur ne voit jamais `<b>...</b` incomplet.
   */
  private htmlChunks(htmlStr: string): Array<{ text: string; isTag: boolean }> {
    const out: Array<{ text: string; isTag: boolean }> = [];
    let i = 0;
    while (i < htmlStr.length) {
      if (htmlStr[i] === '<') {
        const end = htmlStr.indexOf('>', i);
        if (end === -1) { out.push({ text: htmlStr.slice(i), isTag: false }); break; }
        out.push({ text: htmlStr.slice(i, end + 1), isTag: true });
        i = end + 1;
      } else {
        const next = htmlStr.indexOf('<', i);
        if (next === -1) { out.push({ text: htmlStr.slice(i), isTag: false }); break; }
        out.push({ text: htmlStr.slice(i, next), isTag: false });
        i = next;
      }
    }
    return out;
  }

  render() {
    const skippable = this.phase !== 'done';
    const messageClasses = `speaker-message${skippable ? ' is-skippable' : ''}`;
    return html`
      <div class="speaker">
        <div class="speaker-avatar" role="img" aria-label="Sarah">
          ${this.avatarSrc ? html`<img alt="" src=${this.avatarSrc} />` : nothing}
        </div>
        <div
          class=${messageClasses}
          @click=${this.skipToEnd}
        >
          <p class="speaker-name">Sarah</p>
          ${this.phase === 'dots'
            ? html`<div
                class="typing-dots"
                role="status"
                aria-live="polite"
                aria-label="Sarah parle"
              ><span></span><span></span><span></span></div>`
            : html`<div
                class="speaker-msg"
                role="status"
                aria-live="polite"
              >${unsafeHTML(this.displayed)}</div>`}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-speaker': SarahSpeaker;
  }
}
